import React, { useContext, useMemo, useRef } from 'react';
import s from './AccordionItem.scss';
import AccordionHeader from '../Accordion/AccordionHeader/AccordionHeader';
import AccordionPanel from '../Accordion/AccordionPanel/AccordionPanel';
import { cn, guid } from '@express-labs/raven-tools';
import {
  AccordionItemContext,
  AccordionItemContextProps,
} from './AccordionItemContext';
import {
  AccordionContext,
  AccordionContextProps,
} from '../Accordion/AccordionContext';
import { IconV2IconNames } from '../IconV2/IconV2';

export interface AccordionItemProps {
  /** Text and other valid HTML or React Components that will appear in the accordion when expanded. */
  children: React.ReactNode;
  /** Any additional CSS classes to append to the div wrapping this component. */
  className?: string;
  /** Any additional CSS classes to append to a div surrounding the content of the AccordionPanel component. */
  contentClassName?: string;
  /** A string that is unique to this accordion, meaning no other AccordionItems in this Accordion should share this same value. It is used to track the open/closed state of each AccordionItem in a React Context.  If you do re-use this ID on multiple AccordionItems, they will all open/close at the same time. */
  dataAccordionId: string;
  /** The name of an IconV2 icon that appears to the immediate left of the label. See IconV2 for list of possible icons. NOTE: You will also most likely need to tweak the alignment of your chosen IconV2 icon. */
  icon?: IconV2IconNames;
  /** Additional CSS to apply to the icon displayed by the icon prop.  You will most likely need this to tweak the alignment of the icon. */
  iconClassName?: string;
  /** Lets you choose between a caret or a plus and minus symbol. */
  indicatorType?: 'caret' | 'plusminus';
  /** @deprecated no longer does anything. Do not use. (update Storybook docs if this gets used again) */
  isDecorated?: boolean;
  /** Text and other valid HTML or React Components used in the clickable AccordionHeader that, when clicked, toggles expansion / collapse of the AccordionPanel. */
  label: React.ReactNode;
  /** Additional CSS class names to append to the AccordionHeader component  */
  labelClassName?: string;
  /** Additional CSS class names to append to the AccordionPanel component */
  panelClassName?: string;
}

const AccordionItem = ({
  children,
  className,
  contentClassName,
  dataAccordionId,
  icon,
  iconClassName,
  indicatorType = 'caret',
  isDecorated = true,
  label,
  labelClassName,
  panelClassName,
}: AccordionItemProps) => {
  const { expansion, toggleExpansion } =
    useContext<AccordionContextProps>(AccordionContext);

  const panelId = useRef(`accordion-panel-${guid()}`);

  const accordionItemContextValue = useMemo<AccordionItemContextProps>(
    () => ({
      expanded: !!expansion?.[dataAccordionId],
      toggleExpansion: () => toggleExpansion(dataAccordionId),
      panelId: panelId.current,
    }),
    [dataAccordionId, expansion, toggleExpansion, panelId]
  );

  return (
    <AccordionItemContext.Provider value={accordionItemContextValue}>
      <div className={cn(s.accordionItem, className)}>
        <AccordionHeader
          className={labelClassName}
          icon={icon}
          classNameIcon={iconClassName}
          isDecorated={isDecorated}
          indicatorType={indicatorType}
        >
          {label}
        </AccordionHeader>
        <AccordionPanel
          className={panelClassName}
          panelContentClassName={contentClassName}
        >
          {children}
        </AccordionPanel>
      </div>
    </AccordionItemContext.Provider>
  );
};

AccordionItem.displayName = 'AccordionItem';

export default AccordionItem;
